import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LayoutDashboard, Users, Coins, MapPinned, Settings, LogOut } from "lucide-react";
import StatsTab from "@/pages/admin/tabs/StatsTab";
import LeadsTab from "@/pages/admin/tabs/LeadsTab";
import PricingTab from "@/pages/admin/tabs/PricingTab";
import ZonesTab from "@/pages/admin/tabs/ZonesTab";
import SettingsTab from "@/pages/admin/tabs/SettingsTab";

export default function AdminDashboard() {
  const { t } = useLang();
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const tabs = [
    { id: "stats", label: t.admin.tabs.stats, icon: LayoutDashboard },
    { id: "leads", label: t.admin.tabs.leads, icon: Users },
    { id: "pricing", label: t.admin.tabs.pricing, icon: Coins },
    { id: "zones", label: t.admin.tabs.zones, icon: MapPinned },
    { id: "settings", label: t.admin.tabs.settings, icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      <header className="bg-[#1E3A2F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center font-display font-extrabold">S</div>
            <span className="font-display font-bold">{t.admin.dashboard}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/70 hidden sm:block">{admin?.email}</span>
            <button onClick={doLogout} className="flex items-center gap-1.5 text-sm font-semibold bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-1 overflow-x-auto hide-scrollbar border-b border-stone-200 mb-6">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === tb.id ? "border-[#1E3A2F] text-[#1E3A2F]" : "border-transparent text-stone-500 hover:text-stone-800"}`}
              data-testid={`admin-tab-${tb.id}`}
            >
              <tb.icon className="w-4 h-4" /> {tb.label}
            </button>
          ))}
        </div>

        {tab === "stats" && <StatsTab />}
        {tab === "leads" && <LeadsTab />}
        {tab === "pricing" && <PricingTab />}
        {tab === "zones" && <ZonesTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
